import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "./ui/card";
import { Button } from "./ui/button";
import { RefreshCw } from "lucide-react";
import { RELOAD_FLAG, isChunkErrorMessage } from "@/utils/chunkErrorConstants";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary that catches stale-deploy chunk-load failures from
 * lazy-loaded routes and recovers gracefully.
 *
 * On a first chunk-load failure in a tab session, this boundary forces a
 * full reload so the browser pulls the fresh `index.html` with new chunk
 * hashes. On a *second* failure (reload already attempted), it shows an
 * "Update available" card with a manual reload button — clicking that
 * clears the reload-flag so a recoverable retry is possible.
 *
 * For non-chunk errors, no auto-reload is triggered (a reload won't fix a
 * real application bug) and a distinct generic "Something went wrong" card is
 * shown instead of the "Update available" card — a real crash must not be
 * disguised as a routine deploy update. The genuine error is logged to the
 * console so it stays diagnosable.
 *
 * Coordinates with `chunkErrorHandler` and `lazyWithRetry` via the shared
 * `RELOAD_FLAG` sessionStorage key. The global handler runs first (capture
 * phase, before React's commit), so by the time `componentDidCatch` here
 * checks the flag it's already set — preventing a double-reload race.
 *
 * Sentry capture is intentionally skipped on the first failure because
 * React's auto-instrumentation already reports uncaught errors. We only
 * capture on the post-reload path, which is the signal worth investigating.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const isChunk = isChunkErrorMessage(error.message);
    const reloadAlreadyTried = sessionStorage.getItem(RELOAD_FLAG) !== null;

    // Only report when the reload didn't fix it (i.e. it's a real
    // persistent failure, not the expected one-time stale-chunk case).
    // React's global handler reports the first occurrence already.
    if (reloadAlreadyTried) {
      Sentry.captureException(error, {
        tags: { chunk_error: String(isChunk), reload_already_attempted: "true" },
        extra: { componentStack: info.componentStack },
      });
    }

    if (isChunk && !reloadAlreadyTried) {
      sessionStorage.setItem(RELOAD_FLAG, "true");
      // queueMicrotask lets React finish committing the error state before we
      // tear down the page — avoids a flash of the fallback UI.
      queueMicrotask(() => location.reload());
    } else if (!isChunk) {
      // A genuine (non-stale-chunk) crash. Surface it in the console so it is
      // diagnosable rather than silently swallowed behind the fallback card.
      console.error("[ChunkErrorBoundary] uncaught application error:", error);
    }
  }

  // Chunk / stale-deploy recovery: clear the reload-attempt flag so a repeat of
  // the same chunk error can retry the recovery reload once more instead of
  // going straight to the dead-end fallback. Reserved for the chunk path only.
  private handleManualReload = (): void => {
    sessionStorage.removeItem(RELOAD_FLAG);
    location.reload();
  };

  // Generic crash recovery: a plain reload, leaving the chunk reload-attempt
  // flag untouched (it belongs to the stale-deploy path, not application bugs).
  private handleReload = (): void => {
    location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Suppress the fallback flash on the first chunk failure: a reload is
    // already queued via componentDidCatch's microtask. Rendering null
    // (instead of the "Update available" card) avoids a visible flicker
    // between the error commit and the navigation.
    const isChunk =
      this.state.error !== null && isChunkErrorMessage(this.state.error.message);
    const reloadAlreadyTried = sessionStorage.getItem(RELOAD_FLAG) !== null;
    if (isChunk && !reloadAlreadyTried) {
      return null;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    // Chunk / stale-deploy errors keep the "Update available" framing (a reload
    // genuinely fixes them). Every other uncaught error gets a distinct generic
    // card so a real crash is not disguised as a routine deploy update.
    const title = isChunk ? "Update available" : "Something went wrong";
    const description = isChunk
      ? "A new version of the app was deployed. Please reload to continue."
      : "The app hit an unexpected error. Reloading usually clears it — if it keeps happening, contact support.";
    const onReload = isChunk ? this.handleManualReload : this.handleReload;

    return (
      <div className="flex items-center justify-center h-screen bg-[var(--dash-paper)]">
        <Card className="max-w-md rounded-[15px] border-[var(--dash-hairline)] bg-[var(--dash-surface)] shadow-[var(--dash-shadow)]">
          <CardHeader>
            <CardTitle className="tracking-[-0.01em] text-[color:var(--dash-ink)]">
              {title}
            </CardTitle>
            <CardDescription className="text-[color:var(--dash-muted)]">
              {description}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              variant="primary"
              onClick={onReload}
              className="cursor-pointer rounded-[9px] border-0 bg-[var(--dash-accent)] text-[color:var(--dash-band-active-ink)] shadow-none transition-[transform,filter] duration-150 ease-[var(--dash-ease)] hover:bg-[var(--dash-accent)] hover:brightness-95 active:scale-[.97] motion-reduce:transition-none motion-reduce:active:scale-100"
            >
              <RefreshCw data-icon="inline-start" aria-hidden="true" />
              Reload now
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
}
